import config from "../../config.json";

const BASE_AUTH_API = config.BASE_AUTH_API;
const LUD_DOCUMENTS_ENDPOINT = "lud-documents";

const buildUrl = (path = "") => {
  const baseUrl = String(BASE_AUTH_API || "").replace(/\/+$/, "");
  const endpoint = String(path || "").replace(/^\/+/, "");

  return `${baseUrl}/${endpoint}`;
};

const getErrorMessage = (data, response) => {
  if (typeof data === "string" && data.trim()) {
    return data;
  }

  return (
    data?.message ||
    data?.error ||
    `Request failed with status ${response.status}`
  );
};

const parseResponse = async (response) => {
  const contentType = response.headers.get("content-type") || "";

  if (contentType.includes("application/json")) {
    return response.json();
  }

  const text = await response.text();

  if (!text) {
    return {
      success: response.ok,
    };
  }

  return {
    success: response.ok,
    message: text,
  };
};

const request = async ({
  token,
  endpoint,
  method = "GET",
  body,
  isFormData = false,
}) => {
  if (!token) {
    throw new Error("Authentication token is required");
  }

  const headers = {
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
  };

  if (!isFormData && body !== undefined) {
    headers["Content-Type"] = "application/json";
  }

  let response;

  try {
    response = await fetch(buildUrl(endpoint), {
      method,
      headers,
      ...(body !== undefined && {
        body: isFormData ? body : JSON.stringify(body),
      }),
    });
  } catch (error) {
    const networkError = new Error(error?.message || "Failed to fetch");

    networkError.status = 0;
    networkError.data = null;

    throw networkError;
  }

  const data = await parseResponse(response);

  if (!response.ok || data?.success === false) {
    const error = new Error(getErrorMessage(data, response));

    error.status = response.status;
    error.data = data;

    throw error;
  }

  return data;
};

const appendFormValue = (formData, key, value) => {
  if (value === undefined || value === null || value === "") {
    return;
  }

  formData.append(key, String(value));
};

const appendFile = (formData, file) => {
  if (!file) {
    return;
  }

  const isFile = typeof File !== "undefined" && file instanceof File;

  const isBlob = typeof Blob !== "undefined" && file instanceof Blob;

  if (isFile || isBlob) {
    formData.append("file", file);
  }
};

const appendFiles = (formData, files) => {
  const normalizedFiles = Array.isArray(files) ? files : files ? [files] : [];

  normalizedFiles.forEach((file) => {
    const isFile = typeof File !== "undefined" && file instanceof File;

    const isBlob = typeof Blob !== "undefined" && file instanceof Blob;

    if (isFile || isBlob) {
      formData.append("file", file);
    }
  });
};

const encodePathValue = (value) => encodeURIComponent(String(value ?? ""));

/*
|--------------------------------------------------------------------------
| Restaurants
|--------------------------------------------------------------------------
*/

export const getLudDocumentsRestaurantsAPI = async (token, ids) => {
  const params = new URLSearchParams();
  ids.forEach((id) => params.append("id", id));
  const response = await request({
    token,
    endpoint: `${LUD_DOCUMENTS_ENDPOINT}/restaurants?${params.toString()}`,
  });

  return response.restaurants || [];
};

/*
|--------------------------------------------------------------------------
| Documents records
|--------------------------------------------------------------------------
*/

export const getAllLudDocumentsAPI = async (token, ids) => {
  const params = new URLSearchParams();
  ids.forEach((id) => params.append("id", id));
  const response = await request({
    token,
    endpoint: `${LUD_DOCUMENTS_ENDPOINT}?${params.toString()}`,
  });

  return response.documents || [];
};

export const getStoreLudDocumentsAPI = async (token, storeId) => {
  const response = await request({
    token,
    endpoint: `${LUD_DOCUMENTS_ENDPOINT}/${encodePathValue(storeId)}`,
  });

  return response.documents;
};

export const getLudDocumentFileUrlAPI = async (
  token,
  storeId,
  { kind, reference, historyId } = {},
) => {
  const query = new URLSearchParams();
  query.set("kind", String(kind || ""));
  query.set("reference", String(reference || ""));

  if (historyId) {
    query.set("historyId", String(historyId));
  }

  const response = await request({
    token,
    endpoint: `${LUD_DOCUMENTS_ENDPOINT}/${encodePathValue(
      storeId,
    )}/file-url?${query.toString()}`,
  });

  return response;
};

/*
|--------------------------------------------------------------------------
| Admin overview
|--------------------------------------------------------------------------
*/

/*
|--------------------------------------------------------------------------
| Restaurant settings
|--------------------------------------------------------------------------
*/

/*
|--------------------------------------------------------------------------
| Organization documents
|--------------------------------------------------------------------------
*/

export const uploadOrganizationDocumentAPI = async (
  token,
  storeId,
  documentKey,
  file,
  { expiryDate } = {},
) => {
  const formData = new FormData();

  appendFile(formData, file);
  appendFormValue(formData, "expiryDate", expiryDate);

  const response = await request({
    token,
    endpoint: `${LUD_DOCUMENTS_ENDPOINT}/${encodePathValue(
      storeId,
    )}/organization/${encodePathValue(documentKey)}`,
    method: "POST",
    body: formData,
    isFormData: true,
  });

  return response.document;
};

export const updateOrganizationDocumentAPI = async (
  token,
  storeId,
  documentKey,
  { expiryDate, essential } = {},
) => {
  const payload = {};

  if (expiryDate !== undefined) {
    payload.expiryDate = expiryDate;
  }

  if (typeof essential === "boolean") {
    payload.essential = essential;
  }

  const response = await request({
    token,
    endpoint: `${LUD_DOCUMENTS_ENDPOINT}/${encodePathValue(
      storeId,
    )}/organization/${encodePathValue(documentKey)}`,
    method: "PATCH",
    body: payload,
  });

  return response.document;
};

export const deleteOrganizationDocumentAPI = async (
  token,
  storeId,
  documentKey,
) => {
  const response = await request({
    token,
    endpoint: `${LUD_DOCUMENTS_ENDPOINT}/${encodePathValue(
      storeId,
    )}/organization/${encodePathValue(documentKey)}`,
    method: "DELETE",
  });

  return response.document;
};

export const permanentDeleteOrganizationDocumentAPI = async (
  token,
  storeId,
  documentKey,
) => {
  return request({
    token,
    endpoint: `${LUD_DOCUMENTS_ENDPOINT}/${encodePathValue(
      storeId,
    )}/organization/${encodePathValue(documentKey)}/permanent`,
    method: "DELETE",
  });
};

/*
|--------------------------------------------------------------------------
| Contracts
|--------------------------------------------------------------------------
*/

export const createLudContractAPI = async (
  token,
  storeId,
  { file, app, startDate, endDate, status = "active" } = {},
) => {
  const formData = new FormData();

  appendFile(formData, file);
  appendFormValue(formData, "app", app);
  appendFormValue(formData, "startDate", startDate);
  appendFormValue(formData, "endDate", endDate);
  appendFormValue(formData, "status", status);

  const response = await request({
    token,
    endpoint: `${LUD_DOCUMENTS_ENDPOINT}/${encodePathValue(storeId)}/contracts`,
    method: "POST",
    body: formData,
    isFormData: true,
  });

  return response.contract;
};

export const updateLudContractAPI = async (
  token,
  storeId,
  contractId,
  { file, app, startDate, endDate, status } = {},
) => {
  const formData = new FormData();

  appendFile(formData, file);
  appendFormValue(formData, "app", app);
  appendFormValue(formData, "startDate", startDate);
  appendFormValue(formData, "endDate", endDate);
  appendFormValue(formData, "status", status);

  const response = await request({
    token,
    endpoint: `${LUD_DOCUMENTS_ENDPOINT}/${encodePathValue(
      storeId,
    )}/contracts/${encodePathValue(contractId)}`,
    method: "PATCH",
    body: formData,
    isFormData: true,
  });

  return response.contract;
};

export const deleteLudContractAPI = async (token, storeId, contractId) => {
  return request({
    token,
    endpoint: `${LUD_DOCUMENTS_ENDPOINT}/${encodePathValue(
      storeId,
    )}/contracts/${encodePathValue(contractId)}`,
    method: "DELETE",
  });
};

export const permanentDeleteLudContractAPI = async (
  token,
  storeId,
  contractId,
) => {
  return request({
    token,
    endpoint: `${LUD_DOCUMENTS_ENDPOINT}/${encodePathValue(
      storeId,
    )}/contracts/${encodePathValue(contractId)}/permanent`,
    method: "DELETE",
  });
};

export const getLudContractHistoryAPI = async (token, storeId, contractId) => {
  const response = await request({
    token,
    endpoint: `${LUD_DOCUMENTS_ENDPOINT}/${encodePathValue(
      storeId,
    )}/contracts/${encodePathValue(contractId)}/history`,
  });

  return response.history || [];
};

/*
|--------------------------------------------------------------------------
| Account statements
|--------------------------------------------------------------------------
*/

export const createLudStatementAPI = async (
  token,
  storeId,
  { file, app, from, to } = {},
) => {
  const formData = new FormData();

  appendFile(formData, file);
  appendFormValue(formData, "app", app);
  appendFormValue(formData, "from", from);
  appendFormValue(formData, "to", to);

  const response = await request({
    token,
    endpoint: `${LUD_DOCUMENTS_ENDPOINT}/${encodePathValue(
      storeId,
    )}/statements`,
    method: "POST",
    body: formData,
    isFormData: true,
  });

  return response.statement;
};

export const deleteLudStatementAPI = async (token, storeId, statementId) => {
  return request({
    token,
    endpoint: `${LUD_DOCUMENTS_ENDPOINT}/${encodePathValue(
      storeId,
    )}/statements/${encodePathValue(statementId)}`,
    method: "DELETE",
  });
};

/*
|--------------------------------------------------------------------------
| Update requests
|--------------------------------------------------------------------------
*/

export const createLudUpdateRequestAPI = async (
  token,
  storeId,
  { type, note = "", status = "new", file } = {},
) => {
  const formData = new FormData();

  appendFormValue(formData, "type", type);
  appendFormValue(formData, "note", note);
  appendFormValue(formData, "status", status);
  appendFile(formData, file);

  const response = await request({
    token,
    endpoint: `${LUD_DOCUMENTS_ENDPOINT}/${encodePathValue(storeId)}/updates`,
    method: "POST",
    body: formData,
    isFormData: true,
  });

  return response.request;
};

/*
|--------------------------------------------------------------------------
| Reports
|--------------------------------------------------------------------------
*/

// Multipart upload with progress (fetch cannot report upload progress). Resolves the JSON body;
// rejects like request(): error.status, error.data (the backend's { code, message, ... }).
// onProgress(0..1) is called while the file is sent, where the browser supports it.
export const uploadWithProgress = ({ token, endpoint, method, formData, onProgress, signal }) =>
  new Promise((resolve, reject) => {
    if (!token) {
      reject(new Error("Authentication token is required"));
      return;
    }
    const xhr = new XMLHttpRequest();
    xhr.open(method, buildUrl(endpoint));
    if (signal) {
      if (signal.aborted) {
        reject(Object.assign(new Error("Cancelled"), { status: 0, data: { code: "CANCELLED" } }));
        return;
      }
      signal.addEventListener("abort", () => xhr.abort(), { once: true });
    }
    xhr.onabort = () => reject(Object.assign(new Error("Cancelled"), { status: 0, data: { code: "CANCELLED" } }));
    xhr.setRequestHeader("Accept", "application/json");
    xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    if (onProgress && xhr.upload) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && event.total) onProgress(event.loaded / event.total);
      };
    }
    xhr.onerror = () => {
      const error = new Error("Failed to fetch");
      error.status = 0;
      error.data = null;
      reject(error);
    };
    xhr.onload = () => {
      let data = null;
      try {
        data = xhr.responseText ? JSON.parse(xhr.responseText) : null;
      } catch (parseError) {
        data = xhr.responseText ? { message: xhr.responseText } : null;
      }
      if (xhr.status < 200 || xhr.status >= 300 || data?.success === false) {
        const error = new Error(data?.message || data?.error || `Request failed with status ${xhr.status}`);
        error.status = xhr.status;
        error.data = data;
        reject(error);
        return;
      }
      onProgress?.(1);
      resolve(data || { success: true });
    };
    xhr.send(formData);
  });

// Adds every file as a NEW part of the slot (app + type/period + month). legacyAction "add_part"
// confirms adding beside files uploaded before versioning (LEGACY_SLOT_UNRESOLVED).
export const createLudReportFileAPI = async (
  token,
  storeId,
  {
    files,
    file,
    type,
    app,
    year,
    month,
    period = "other",
    coverageFrom,
    coverageTo,
    legacyAction,
    onProgress,
  } = {},
) => {
  const formData = new FormData();

  appendFormValue(formData, "type", type);
  appendFormValue(formData, "app", app);
  appendFormValue(formData, "year", year);
  appendFormValue(formData, "month", month);
  appendFormValue(formData, "period", period);
  appendFormValue(formData, "coverageFrom", coverageFrom);
  appendFormValue(formData, "coverageTo", coverageTo);
  appendFormValue(formData, "legacyAction", legacyAction);
  appendFiles(formData, files || file);

  const response = await uploadWithProgress({
    token,
    endpoint: `${LUD_DOCUMENTS_ENDPOINT}/${encodePathValue(storeId)}/reports`,
    method: "POST",
    formData,
    onProgress,
  });

  return response.reports || (response.report ? [response.report] : []);
};

// Replaces the current file of ONE part. expectedVersion is the version shown to the user; a newer
// committed version answers 409 VERSION_CONFLICT. -> { report, noop, cleanupPending }
export const replaceLudReportFileAPI = async (
  token,
  storeId,
  reportId,
  { file, expectedVersion, onProgress } = {},
) => {
  const formData = new FormData();

  appendFormValue(formData, "expectedVersion", expectedVersion);
  appendFile(formData, file);

  const response = await uploadWithProgress({
    token,
    endpoint: `${LUD_DOCUMENTS_ENDPOINT}/${encodePathValue(storeId)}/reports/${encodePathValue(reportId)}`,
    method: "PATCH",
    formData,
    onProgress,
  });

  return { report: response.report, noop: Boolean(response.noop), cleanupPending: Boolean(response.cleanupPending) };
};

export const deleteLudReportFileAPI = async (token, storeId, reportId) => {
  return request({
    token,
    endpoint: `${LUD_DOCUMENTS_ENDPOINT}/${encodePathValue(
      storeId,
    )}/reports/${encodePathValue(reportId)}`,
    method: "DELETE",
  });
};
